'use client';

import { createInvoice } from '@/lib/paydunya';
import { Product } from '@/payload-types';
import { Button } from '@/ui/shadcn/button';
import { Input } from '@/ui/shadcn/input';
import { Label } from '@/ui/shadcn/label';
import { logger } from '@/lib/logger';
import React, { useRef, useState } from 'react';

export default function OrderForm({ product, user }: { product: Product, user: any }) {
    const formRef = useRef<HTMLFormElement>(null);
    const [errors, setErrors] = useState<Record<string, boolean>>({});

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrors({});
        
        try {
            const formData = new FormData(formRef.current!);
            
            // Validation des champs
            const fields = ['nom', 'prenom', 'email', 'telephone', 'adresse', 'ville', 'codePostal', 'pays'];
            const newErrors: Record<string, boolean> = {};
            let hasErrors = false;

            fields.forEach(field => {
                const value = formData.get(field) as string;
                if (!value || value.trim() === '') {
                    newErrors[field] = true;
                    hasErrors = true;
                } else {
                    newErrors[field] = false;
                }
            });

            setErrors(newErrors);

            if (hasErrors) {
                logger.warn('Form validation failed: All fields are required');
                return;
            }
            
            const orderData = {
                customer_id: user?.id || null,
                firstName: formData.get('nom') as string,
                lastName: formData.get('prenom') as string,
                email: formData.get('email') as string,
                phone: formData.get('telephone') as string,
                address: formData.get('adresse') as string,
                city: formData.get('ville') as string,
                zip: formData.get('codePostal') as string,
                country: formData.get('pays') as string,
            };

            
            const data = await createInvoice(product, orderData);
            window.location.href = data.response_text;

        } catch (error) {
            logger.error('Error creating order', error);
        }
    }
    
    return (
        <form ref={formRef} onSubmit={handleSubmit} className='w-full lg:w-1/2'>
            <div className="mb-6">
                <h1 className='text-2xl font-bold'>
                    Confirmez votre commande
                </h1>
                <p className='text-gray-500'>
                    Veuillez entrer les informations de livraison pour finaliser votre commande.
                </p>
            </div>

            <hr className='my-8' />

            <h2 className='text-2xl font-bold mb-4'>Informations personnelles</h2>

            <div className='grid grid-cols-2 gap-4'>
                <div>
                    <Label className='mb-2'>Nom</Label>
                    <Input 
                        name="nom" 
                        type="text" 
                        placeholder="Nom" 
                        defaultValue={user?.name?.split(' ')[0]}
                        variant={errors.nom ? "error" : "default"}
                    />
                </div>
                <div>
                    <Label className='mb-2'>Prénom</Label>
                    <Input 
                        name="prenom" 
                        type="text" 
                        placeholder="Prénom" 
                        defaultValue={user?.name?.split(' ')[1] || ''}
                        variant={errors.prenom ? "error" : "default"}
                    />
                </div>
                <div>
                    <Label className='mb-2'>Email</Label>
                    <Input 
                        name="email" 
                        type="email" 
                        placeholder="Email" 
                        defaultValue={!user?.invalidEmail ? user?.email : ''}
                        variant={errors.email ? "error" : "default"}
                    />
                </div>
                <div>
                    <Label className='mb-2'>Téléphone</Label>
                    <Input 
                        name="telephone" 
                        type="tel" 
                        placeholder="Téléphone" 
                        defaultValue={user?.phone}
                        variant={errors.telephone ? "error" : "default"}
                    />
                </div>
            </div>

            <hr className='my-8' />

            <h2 className='text-2xl font-bold mb-4'>Adresse de livraison</h2>

            <div className='grid grid-cols-2 gap-4 mb-8'>
                <div>
                    <Label className='mb-2'>Adresse</Label>
                    <Input 
                        name="adresse" 
                        type="text" 
                        placeholder="Adresse" 
                        variant={errors.adresse ? "error" : "default"}
                    />
                </div>
                <div>
                    <Label className='mb-2'>Ville</Label>
                    <Input 
                        name="ville" 
                        type="text" 
                        placeholder="Ville" 
                        variant={errors.ville ? "error" : "default"}
                    />
                </div>
                <div>
                    <Label className='mb-2'>Code postal</Label>
                    <Input 
                        name="codePostal" 
                        type="text" 
                        placeholder="Code postal" 
                        variant={errors.codePostal ? "error" : "default"}
                    />
                </div>
                <div>
                    <Label className='mb-2'>Pays</Label>
                    <Input 
                        name="pays" 
                        type="text" 
                        placeholder="Pays"
                        variant={errors.pays ? "error" : "default"}
                    />
                </div>
            </div>

            <Button type="submit">
                <p>Continuer</p>
            </Button>
        </form>
    );
};
